"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Target, Users, MousePointerClick, CircleCheck as CheckCircle2, ArrowRight, MapPin, CalendarCheck, BookOpen } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const metrics = [{
  icon: Target,
  label: "Page 1 Local Keywords",
  value: "13 of 17",
  subtext: "7 in #1 position (up from 11/17 at 2 mos)",
  badge: "7 in #1 Spot"
}, {
  icon: TrendingUp,
  label: "Year 1 Traffic Growth",
  value: "+100%",
  subtext: "+30% lift achieved within first 3 months",
  badge: "Verified Lift"
}, {
  icon: Users,
  label: "New Daily Readers",
  value: "25 / day",
  subtext: "Steady influx of organic readership",
  badge: "Sustained"
}, {
  icon: MousePointerClick,
  label: "Monthly Search Clicks",
  value: "200+",
  subtext: "High-intent organic search clicks",
  badge: "Organic Search"
}];
const timelineSteps = [{
  phase: "Phase 1: The Challenge",
  title: "Building Authority from Scratch Against High-Competition National Terms",
  description: "As a brand-new online publication without a legacy print edition or established link equity, the magazine faced severe organic visibility hurdles. Ranking directly for high-competition, broad national keywords was unrealistic without foundational domain authority and trust signals.",
  points: ["Zero initial search authority or backlink profile for competitive national topics", "Critical dependence on search discoverability to drive audience monetization", "Stiff competition against high-authority national publishing giants"]
}, {
  phase: "Phase 2: The Strategy",
  title: "An Organic Strategy With A Local Twist",
  description: "Rather than burning resources targeting broad national keywords immediately, I designed an unconventional staged approach: capture hyper-relevant, geo-targeted search terms first. By establishing immediate local dominance, we built indexation credibility and domain authority much faster, laying the runway for broader national organic expansion.",
  points: ["Curated 17 strategic localized intent keywords with high search relevance", "Engineered a scalable content structure designed for fast indexing and local rank capture", "Designed an internal linking model to transfer local authority to broader organic hub pages"]
}, {
  phase: "Phase 3: Implementation",
  title: "Hands-On Execution & Phased Keyword Migration",
  description: "I managed and executed the entire roadmap directly—from technical architecture and semantic on-page copy to citation consistency. Within 2 months, 11 of the 17 localized targets secured Page 1 rankings (with 5 in position #1). By month 4, we expanded this to 13 of 17 keywords on Page 1 (with 7 claiming the absolute top spot), initiating the planned pivot toward high-volume national topics.",
  points: ["2 Months: 11 of 17 local keywords on Page 1 (5 claiming the #1 top spot)", "4 Months: 13 of 17 local keywords on Page 1 (7 claiming the #1 top spot)", "Systematic transition of accumulated domain equity into competitive national topics"]
}, {
  phase: "Phase 4: Verified Results",
  title: "+100% YoY Growth & Long-Term Reader Monetization",
  description: "The local-to-national SEO flywheel generated immediate momentum: organic traffic grew 30% in the first 3 months and doubled (+100%) by the close of year one. The publication now captures an average of 25 new readers per day and over 200 monthly clicks from high-intent search queries, securing lasting editorial discoverability.",
  points: ["+30% organic traffic increase in first 3 months; +100% overall increase after Year 1", "25 average new readers per day and 200+ monthly qualified search clicks", "Successful transformation into a sustained, monetized digital publication"]
}];
export default function CaseStudyDetail() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="localseocasestudydetail" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
              <MapPin className="size-3.5 text-primary" />
              Verified Case Study
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
            Starting Local to Go Global: The Online Magazine Blueprint
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            How I applied an agile local-first organic strategy to help a brand-new digital publication achieve 
            <strong className="text-foreground font-semibold"> +100% Year 1 growth</strong> and 13 Page 1 rankings (with 7 in position #1).
          </p>
        </motion.div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {metrics.map((metric, i) => {
          const Icon = metric.icon;
          return <motion.div key={metric.label} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }}>
                <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {metric.badge}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-3xl font-bold font-default text-foreground tracking-tight mb-1">
                        {metric.value}
                      </div>
                      <div className="font-medium text-sm text-foreground mb-1">
                        {metric.label}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {metric.subtext}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Narrative & Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          
          {/* Main Case Timeline Breakdown */}
          <div className="lg:col-span-8 space-y-8">
            <div className="border-b border-border pb-4 mb-2">
              <h3 className="text-2xl font-bold font-default text-foreground">
                Challenge → Strategy → Implementation → Verified Results
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                A granular, transparent look into the four-stage execution of this organic search campaign.
              </p>
            </div>

            {timelineSteps.map((step, i) => <motion.div key={step.phase} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="relative pl-6 sm:pl-8 border-l-2 border-primary/20 hover:border-primary transition-[border-color]">
                <div className="absolute -left-[9px] top-1.5 size-4 rounded-lg bg-primary ring-4 ring-background" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wider block mb-1">
                  {step.phase}
                </span>
                <h4 className="text-xl font-bold font-default text-foreground mb-3">
                  {step.title}
                </h4>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-4">
                  {step.description}
                </p>
                <ul className="space-y-2 mb-2">
                  {step.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{pt}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>

          {/* Project Facts & Conversion Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <motion.div initial={{
            opacity: 0,
            x: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }}>
              <Card className="border border-border bg-card shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-secondary text-secondary-foreground">
                      <BookOpen className="size-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Project Facts</CardTitle>
                      <CardDescription className="text-xs">Campaign parameters &amp; core milestones</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <div className="space-y-2.5 text-xs">
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Industry:</span>
                      <span className="font-semibold">Online Publishing</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Strategy:</span>
                      <span className="font-semibold">Local-First to National SEO</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Core Keyword Pool:</span>
                      <span className="font-semibold">17 High-Intent Terms</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Month 2 Page 1:</span>
                      <span className="font-semibold">11 Keywords (5 in #1)</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Month 4 Page 1:</span>
                      <span className="font-semibold text-primary">13 Keywords (7 in #1)</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">3-Month Velocity:</span>
                      <span className="font-semibold">+30% Traffic Growth</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Year 1 Lift:</span>
                      <span className="font-semibold text-primary">+100% Verified Growth</span>
                    </div>
                    <div className="flex justify-between items-center text-foreground">
                      <span className="text-muted-foreground">Audience Influx:</span>
                      <span className="font-semibold">25 New Readers / Day</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }}>
              <Card className="border border-border bg-primary text-primary-foreground shadow-md">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <CalendarCheck className="size-5" />
                    <h4 className="font-bold text-base font-default">Ready for Similar Growth?</h4>
                  </div>
                  <p className="text-sm opacity-90 leading-relaxed">
                    Let’s review your website’s current authority and construct a tailored, staged roadmap to dominate high-value organic search terms.
                  </p>
                  <Button asChild variant="secondary" className="w-full justify-center">
                    <Link to="/ContactPage">
                      Book a Free SEO Consultation
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

        </div>

        {/* Bottom CTA / More Studies Banner */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-xl border border-border bg-card p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left space-y-1">
            <h4 className="text-lg font-bold font-default text-foreground">
              Explore More Case Studies
            </h4>
            <p className="text-sm text-muted-foreground">
              Review verified results across National SEO, Technical SEO, and Ecommerce campaigns.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="outline">
              <Link to="/CaseStudiesPage">
                All Case Studies
              </Link>
            </Button>
            <Button asChild variant="default">
              <Link to="/LocalSEOPage">
                Local SEO Services
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}